import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useQuery } from '../hooks/useQuery'
import { iOrderVal, iParamsMsgs } from '../types/api.types'
import { CheckedContextProvider } from './CheckedContext'

interface iTableProvider {
  children: React.ReactNode
  useStoredData: boolean
  type: string
}

export const TABLE_TYPE = {
  OUT: 'out',
  IN: 'in',
  SUBM: 'subm'
}

const KEY_LS = {
  LIMIT: 'podo_ekom_limit',
  PAGE: 'podo_ekom_page',
  ALL: 'podo_ekom_filter_all'
}

const initState = {
  page: 1,
  limit: parseInt(window.localStorage.getItem(KEY_LS.LIMIT) || '20'),
  order: 'desc' as iOrderVal,
  orderBy: 'deliveredAt',
  deliveredAtBefore: '',
  deliveredAtAfter: '',
  searchSubject: '',
  searchObject: '',
  searchOrg: '',
  isRead: false,
  isUnRead: false,
  msgType: '',
  msgState: ''
}

const TableContext = React.createContext({
  state: initState,
  useStoredData: true,
  type: '',
  setState: (state: typeof initState) => {},
  updateState: (state: Partial<typeof initState>) => {},
  resetState: () => {}
})

export const useQueryToTableProvider = () => {

  const {
    type,
    updateState,
    useStoredData,
    state: {
      page,
      limit,
      searchObject,
      searchSubject,
      searchOrg,
      deliveredAtBefore,
      deliveredAtAfter,
      orderBy,
      order,
      isRead,
      isUnRead,
      msgType,
      msgState,
    }
  } = useTableContext()

  const query = useQuery()
  const qpage = query.get(type + 'page')

  const history = useHistory()

  useEffect(() => {
    if (page !== initState.page) {
      query.set(type + 'page', page.toString())
      history.push({ search: query.toString() })
    } else {
      if (query.get(type + 'page')) {
        query.delete(type + 'page')
        history.push({ search: query.toString() })
      }
    }
  }, [page])

  useEffect(() => {
    if (qpage) {
      updateState({ page: parseInt(qpage) })
    }
  }, [])

  useEffect(() => {
    window.localStorage.setItem(KEY_LS.LIMIT, limit.toString())
  }, [limit])

  useEffect(() => {
    window.localStorage.setItem(KEY_LS.PAGE + type, page.toString())
  }, [page])

  useEffect(() => {
    if (useStoredData) {
      const data = {
        searchObject,
        searchSubject,
        searchOrg,
        deliveredAtAfter,
        deliveredAtBefore,
        order,
        orderBy,
        isRead,
        isUnRead,
        msgType,
        msgState,
      }
      window.localStorage.setItem(KEY_LS.ALL, JSON.stringify(data))
    }
  }, [searchObject, searchSubject, searchOrg, deliveredAtAfter, deliveredAtBefore, order, orderBy, isRead, isUnRead, msgType, msgState])

}

const TableContextProvider = ({ children, useStoredData, type }: iTableProvider) => {
  const statesStoredLocale = JSON.parse(window.localStorage.getItem(KEY_LS.ALL) || '{"order":"desc","orderBy":"deliveredAt"}')
  const storedLimit = parseInt(window.localStorage.getItem(KEY_LS.LIMIT) || '20')
  const storedPage = parseInt(window.localStorage.getItem(KEY_LS.PAGE + type) || '1')

  const storedState: typeof initState = {
    ...statesStoredLocale,
    page: storedPage,
    limit: storedLimit,
  }

  const [state, setTableState] = useState(useStoredData ? storedState : initState)
  const providerValue = React.useMemo(() => ({
    state,
    setState: setTableState,
    type: type,
    useStoredData: useStoredData,
    updateState: (values: Partial<typeof initState>) => {
      setTableState(state => ({ ...state, ...values }))
    },
    resetState: () => {
      setTableState(initState)
    }
  }), [state])

  return (
    <TableContext.Provider value={providerValue}>
      {children}
    </TableContext.Provider>
  )
}

export const withTableContext = (WrapedComp: any, type: string, useStoredData: boolean = true) => (props: any) =>
  <TableContextProvider useStoredData={useStoredData} type={type}>
    <CheckedContextProvider>
      <WrapedComp {...props}/>
    </CheckedContextProvider>
  </TableContextProvider>

export const useTableContext = () => useContext(TableContext)

export const useTableContextParams = (): iParamsMsgs => {
  const {
    state: {
      page,
      limit,
      order,
      orderBy,
      deliveredAtBefore,
      deliveredAtAfter,
      searchSubject,
      searchOrg,
      searchObject,
      isRead,
      isUnRead,
      msgType,
      msgState
    },
    type
  } = useTableContext()

  const isTableTypeOut = [TABLE_TYPE.OUT].includes(type)
  const isTableTypeIn = [TABLE_TYPE.IN].includes(type)
  const isTableTypeSubm = [TABLE_TYPE.SUBM].includes(type)
  const params = useMemo(() => ({
    page,
    limit,
    order: orderBy ? { [orderBy]: order } : undefined,
    deliveredAt: deliveredAtAfter || deliveredAtBefore ? {
      before: deliveredAtBefore || undefined,
      after: deliveredAtAfter || undefined
    } : undefined,
    is_read: !isTableTypeSubm ? (isRead ? true : isUnRead ? false : undefined) : undefined,
    subject: !isTableTypeSubm ? searchSubject || undefined : undefined,
    objectName: searchObject || undefined,
    senderOrganizationName: (isTableTypeIn && searchOrg) ? searchOrg : undefined,
    recipientOrganizationName: ((isTableTypeOut || isTableTypeSubm) && searchOrg) ? searchOrg : undefined,
    type: isTableTypeSubm ? msgType || undefined : undefined,
    state: isTableTypeSubm ? msgState || undefined : undefined
  }), [page, limit, order, orderBy, searchSubject, searchOrg, searchObject, deliveredAtAfter, deliveredAtBefore, isRead, isUnRead, msgType, msgState])

  return params
}
